/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import Slider, { Settings as SliderSettings } from 'react-slick'
import styled from 'styled-components'
import colors from '@sportninja/common/constants/appColors'
import type { SnEvent } from '@sportninja/common/features/event/types'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import UpcomingEventCard from './UpcomingEventCard'

const PrevArrow = (props: any) => {
  const { className, onClick, currentSlide } = props
  return (
    <div
      className={`${className} ${currentSlide === 0 ? 'slick-disabled' : ''}`}
      onClick={onClick}
      style={{ display: currentSlide === 0 ? 'none' : 'block' }}
    />
  )
}

const NextArrow = (props: any) => {
  const { className, onClick, slideCount, currentSlide } = props
  return (
    <div
      className={`${className} ${
        currentSlide === slideCount - 1 ? 'slick-disabled' : ''
      }`}
      onClick={onClick}
      style={{ display: currentSlide === slideCount - 1 ? 'none' : 'block' }}
    />
  )
}

interface UpcomingEventsWidgetProps {
  events: SnEvent[]
}

/**
 * The viewer's next events in the home sidebar, paged one at a time.
 *
 * A sibling to the game carousel above it, and deliberately the SAME chrome:
 * react-slick with one slide shown, arrows either side and dots below, so the
 * two read as one idiom down the sidebar rather than as different kinds of
 * block.
 *
 * Renders NOTHING when there are no events — the widget disappears rather than
 * showing a permanent empty box explaining the user has no events, which is
 * what the game carousel beside it already does.
 *
 * No loading state: the sidebar's convention is to render nothing until data
 * arrives, so a widget never occupies space it may not keep.
 */
const UpcomingEventsWidget: React.FC<UpcomingEventsWidgetProps> = ({
  events,
}) => {
  if (!events || events.length === 0) {
    return null
  }

  const settings: SliderSettings = {
    // Dots only when there is somewhere to page to — the game carousel's own
    // rule. A single dot communicates nothing.
    dots: events.length > 1,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    customPaging: () => <CustomDot />,
    className: 'slick-carousel',
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    adaptiveHeight: true,
  }

  return (
    <CarouselContainer $hasDots={events.length > 1}>
      <StyledSlider {...settings}>
        {events.map((event) => (
          <div key={event.id}>
            <UpcomingEventCard event={event} />
          </div>
        ))}
      </StyledSlider>
    </CarouselContainer>
  )
}

export default UpcomingEventsWidget

/* The dot row is in normal flow (see StyledSlider), carrying its own 16px
   margin-top. A flat 16px here puts the same 16px below it, so the dots sit
   centred between the two widgets — and the widget-to-widget distance stays
   the sidebar's standard 32px + the dot row. Without dots the container needs
   the plain 32px itself. */
const CarouselContainer = styled.div<{ $hasDots: boolean }>`
  max-width: 400px;
  margin: 0 auto;
  margin-bottom: ${({ $hasDots }) => ($hasDots ? 16 : 32)}px;
`

/* Mirrors GameAttendanceCarousel's slider chrome exactly — same dot row
   geometry, same arrow inset, same slide gutters — so the two carousels in the
   sidebar behave and measure identically. */
const StyledSlider = styled(Slider)`
  /* The dot row is taken OUT of slick's absolute positioning and put back in
     normal flow. As shipped it is position:absolute / bottom:-24px, which
     pins the gap ABOVE the dots to ~4px — a value margin-bottom cannot
     influence, so the row always hugs the card and every attempt to even out
     the spacing only grew the gap below it.

     In flow, its own margin-top sets the gap above and the container's
     margin-bottom sets the gap below, so the two can actually be matched. */
  .slick-dots {
    position: static;
    display: flex !important;
    justify-content: center;
    align-items: center;
    margin-top: 16px;
    gap: 8px;
    li {
      margin: 0 0;
      width: 8px !important;
      height: 8px !important;
      /* The shipped button is 20x20 with 5px padding, which would set the
         row's height regardless of the 8px li. Matching it to the li keeps the
         row exactly as tall as the dots it shows. */
      button {
        width: 8px !important;
        height: 8px !important;
        padding: 0;
      }
    }
  }

  /* slick centres its arrows on top:50% of the WHOLE slider. Now that the
     dot row is in normal flow, the slider is taller than the slide itself, so
     that 50% lands below the card's centre. Offsetting by half the dot block
     (its 16px margin-top + 8px dots = 24, so 12px) pulls them back onto the
     card's own vertical centre. */
  .slick-arrow {
    margin: 0 16px;
    z-index: 1;
    transform: translateY(calc(-50% - 12px));
  }

  .slick-slide {
    padding: 0 8px;
  }
  .slick-list {
    margin: 0 -8px;
  }
`

const CustomDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7f8c8d;

  .slick-active & {
    background-color: ${colors.PRIMARY};
  }
`
